import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogContainer,
  Heading,
  Content,
  Button,
  Flex,
  View,
  TextField,
  Picker,
  Item,
} from "@adobe/react-spectrum";
import { ToastQueue } from '@adobe/react-spectrum';
import { useTranslation } from "react-i18next";
import { useDependency } from "../../../contexts/DependencyProvider";
import { ListItemRequest } from "../../../services/soap/item/requests/ListItemRequest";
import { GetItemRequest } from "../../../services/soap/item/requests/GetItemRequest";
import TreeGridComponent from "../TreeGridComponent/TreeGridComponent";
import { TreeGridRow, TreeGridColumn } from "../TreeGridComponent/types";
import { useDeviceDetection } from "../../../hooks/UseDeviceDetection";

interface ProductSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedProducts: SelectedProduct[]) => Promise<void>;
}

export interface SelectedProduct {
  id: string;
  partID: string;
  partNumber: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  currency: string;
  vatValue: number;
  vatName: string;
  amountWithVat: number;
  purchasePrice: number;
  productLine: string;
  description: string;
  components?: ProductComponent[];
}

interface ProductComponent {
  component: string;
  componentName: string;
  amount: string;
  amountWithVat: string;
  purchasePrice: string;
  currencyName: string;
  vatName: string;
  vatValue: string;
  isParent: string;
  countMin: string;
  countMax: string;
  priceInclude: string;
  ratio: string;
}

interface ProductItem {
  id: string;
  name: string;
  refId: string;
  amount: string;
  amountWithVat: string;
  purchasePrice: string;
  currencyName: string;
  vatName: string;
  vatValue: string;
  typeName: string;
  statusName: string;
  quantity: string;
  COMPONENT?: ProductComponent[];
}

export default function ProductSelectionModal({
  isOpen,
  onClose,
  onSave,
}: ProductSelectionModalProps) {
  const { t } = useTranslation();
  const _t = useCallback(
    (key: string, defaultValue?: string, options?: Record<string, any>) =>
      t(key, {
        ns: "layout_components",
        ...(defaultValue !== undefined ? { defaultValue } : {}),
        ...(options || {}),
      }),
    [t],
  );
  const { store, itemService } = useDependency();
  const { shouldUseMobileLayout } = useDeviceDetection();
  const isMobileLayout = shouldUseMobileLayout();
  const PAGE_SIZE = 1000;

  // State
  const [availableProducts, setAvailableProducts] = useState<TreeGridRow[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<TreeGridRow[]>([]);
  const [selectedProductsForAdd, setSelectedProductsForAdd] = useState<
    Set<string>
  >(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchRefId, setSearchRefId] = useState("");
  const [priceListFilter, setSelectedPriceList] = useState("all");
  const [totalCount, setTotalCount] = useState(0);
  const [selectionClearKey, setSelectionClearKey] = useState(0);
  const latestRequestIdRef = useRef(0);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 900,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => setViewportHeight(window.innerHeight);
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const desktopDialogHeight = useMemo(() => {
    const adjustedHeight = viewportHeight - 150;
    return `${Math.min(Math.max(adjustedHeight, 620), 820)}px`;
  }, [viewportHeight]);

  const desktopGridHeight = useMemo(() => {
    const availableHeight = viewportHeight - 470;
    return Math.min(Math.max(Math.floor(availableHeight / 2), 190), 290);
  }, [viewportHeight]);
  const selectedProductsParentCount = useMemo(
    () => selectedProducts.filter((r) => r.parentId === null).length,
    [selectedProducts],
  );
  const emptySelectedGridHeight = Math.max(
    Math.min(desktopGridHeight - 140, 140),
    96,
  );
  const selectedGridMinHeight = isMobileLayout
    ? "260px"
    : `${selectedProductsParentCount === 0 ? emptySelectedGridHeight : desktopGridHeight}px`;

  // Transform product to TreeGridRow
  const transformProductToRow = useCallback(
    (product: ProductItem): TreeGridRow => ({
      id: product.id,
      parentId: null,
      depth: 0,
      childrenIds: [],
      isExpanded: false,
      rowType: "product",
      sortOrder: 0,
      data: {
        code: product.refId || "",
        productName: product.name,
        standardPrice: parseFloat(product.amount) || 0,
        vatPercent: parseFloat(product.vatValue) || 0,
        productLine: product.typeName || "",
        description: product.name || "",
        currency: product.currencyName || "",
        vatName: product.vatName || "",
        amountWithVat: parseFloat(product.amountWithVat) || 0,
        purchasePrice: parseFloat(product.purchasePrice) || 0,
        quantity: 1,
        _original: product,
      },
    }),
    [],
  );

  // Transform product with components to TreeGridRows
  const transformProductWithComponentsToRows = useCallback(
    (product: any, components: ProductComponent[] | null): TreeGridRow[] => {
      const rows: TreeGridRow[] = [];
      const parentId = `selected-${product.id}-${Date.now()}`;

      // Child component rows
      const childRows: TreeGridRow[] = [];
      if (components && components.length > 0) {
        components.forEach((comp, index) => {
          if (comp.isParent === "1") return;

          const childId = `${parentId}-child-${index}`;
          const compAmount = parseFloat(comp.amount) || 0;

          childRows.push({
            id: childId,
            parentId: parentId,
            depth: 1,
            childrenIds: [],
            isExpanded: false,
            rowType: "product",
            sortOrder: index,
            data: {
              code: "",
              productName: comp.componentName,
              standardPrice: compAmount,
              vatPercent: parseFloat(comp.vatValue) || 0,
              productLine: "",
              description: comp.componentName,
              currency: comp.currencyName || "",
              quantity: parseFloat(comp.countMin) || 1,
              isComponent: true,
              _original: comp,
            },
          });
        });
      }

      // Parent row
      rows.push({
        id: parentId,
        parentId: null,
        depth: 0,
        childrenIds: childRows.map((r) => r.id),
        isExpanded: childRows.length > 0,
        rowType: "product",
        sortOrder: 0,
        data: {
          code: product.refId || "",
          productName: product.name,
          standardPrice: parseFloat(product.amount) || 0,
          vatPercent: parseFloat(product.vatValue) || 0,
          productLine: product.typeName || "",
          description: product.name || "",
          currency: product.currencyName || "",
          vatName: product.vatName || "",
          amountWithVat: parseFloat(product.amountWithVat) || 0,
          purchasePrice: parseFloat(product.purchasePrice) || 0,
          quantity: 1,
          hasComponents: childRows.length > 0,
          productId: product.id,
          _original: product,
        },
      });

      rows.push(...childRows);
      return rows;
    },
    [],
  );

  // Load products
  const loadProducts = useCallback(
    async (reset: boolean = false) => {
      const requestId = ++latestRequestIdRef.current;

      if (reset) {
        setIsLoading(true);
        setCurrentOffset(0);
        setHasMore(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const offset = reset ? 0 : currentOffset;
        const response = await itemService.listItem(
          new ListItemRequest(
            store.Server,
            store.SessionId,
            PAGE_SIZE,
            offset,
            {
              orderBy: "name",
              statusShow: "1",
              ...(searchTerm && { searchName: searchTerm }),
              ...(searchRefId && { refId: searchRefId }),
            },
          ),
        );

        if (response.result === "OK") {
          if (requestId !== latestRequestIdRef.current) {
            return;
          }

          let items: ProductItem[] = [];
          if (Array.isArray(response.ITEM)) {
            items = response.ITEM as ProductItem[];
          } else if (response.ITEM) {
            items = [response.ITEM as ProductItem];
          }

          const rows = items.map(transformProductToRow);

          if (reset) {
            setAvailableProducts(rows);
            setCurrentOffset(PAGE_SIZE);
          } else {
            setAvailableProducts((prev) => [...prev, ...rows]);
            setCurrentOffset((prev) => prev + PAGE_SIZE);
          }

          const total = response.count || 0;
          setTotalCount(total);
          const newOffset = reset ? PAGE_SIZE : offset + PAGE_SIZE;
          setHasMore(newOffset < total && items.length === PAGE_SIZE);
        } else {
          if (requestId !== latestRequestIdRef.current) {
            return;
          }

          if (reset) {
            setAvailableProducts([]);
          }
          setHasMore(false);
        }
      } catch (error) {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        console.error("Error loading products:", error);
        ToastQueue.negative(
          _t("failed_to_load_products", "Failed to load products"),
          { timeout: 3000 },
        );
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [
      itemService,
      store.Server,
      store.SessionId,
      currentOffset,
      searchTerm,
      searchRefId,
      transformProductToRow,
    ],
  );

  // Load initial products
  useEffect(() => {
    if (isOpen) {
      loadProducts(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      loadProducts(true);
    }, 700);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, searchRefId]);

  // Load more products
  const loadMoreProducts = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    loadProducts(false);
  }, [isLoadingMore, hasMore, loadProducts]);

  // Fetch product with components
  const fetchProductWithComponents = useCallback(
    async (
      productId: string,
    ): Promise<{ product: any; components: ProductComponent[] | null }> => {
      try {
        const response = await itemService.getItem(
          new GetItemRequest(store.Server, store.SessionId, productId),
        );

        if (response.result === "OK") {
          const product = Array.isArray(response.ITEM)
            ? response.ITEM[0]
            : response.ITEM;
          let components: ProductComponent[] | null = null;

          if (product?.COMPONENT) {
            const comps = Array.isArray(product.COMPONENT)
              ? product.COMPONENT
              : [product.COMPONENT];
            components = comps.filter(
              (c: ProductComponent) => c.isParent !== "1",
            );
          }

          return { product, components };
        }

        throw new Error(
          _t("failed_to_load_product_details", "Failed to load product details"),
        );
      } catch (error) {
        console.error("Error fetching product details:", error);
        throw error;
      }
    },
    [itemService, store.Server, store.SessionId],
  );

  // Handle drag from available products to selected products
  const handleExternalDropToSelected = useCallback(
    async (
      rows: TreeGridRow[],
      sourceGridId: string,
      targetRowId: string | null,
      position: "before" | "after" | "child",
    ) => {
      if (sourceGridId !== "available-products") return;

      setIsLoading(true);
      try {
        const newSelectedRows: TreeGridRow[] = [];

        for (const row of rows) {
          const productId = row.id;

          // Check if product is already selected
          const alreadySelected = selectedProducts.some(
            (sp) => sp.data.productId === productId || sp.id === productId,
          );

          if (alreadySelected) {
            ToastQueue.info(
              _t("product_already_selected", "{{name}} is already selected", {
                name: row.data.productName,
              }),
              {
              timeout: 2000,
              },
            );
            continue;
          }

          // Fetch product with components
          const { product, components } =
            await fetchProductWithComponents(productId);

          if (product) {
            const productRows = transformProductWithComponentsToRows(
              product,
              components,
            );
            newSelectedRows.push(...productRows);
          }
        }

        if (newSelectedRows.length > 0) {
          setSelectedProducts((prev) => [...prev, ...newSelectedRows]);
          // Clear selections from available products grid
          const addedProductIds = rows.map((r) => r.id);
          setSelectedProductsForAdd((prev) => {
            const newSet = new Set(prev);
            addedProductIds.forEach((id) => newSet.delete(id));
            return newSet;
          });
          // Force clear checkbox selections
          setSelectionClearKey((prev) => prev + 1);
          ToastQueue.positive(
            t("products_added_to_selection", {
              ns: "layout_components",
              count: rows.length,
              defaultValue: "Added {{count}} product(s) to selection",
            }),
            { timeout: 3000 },
          );
        }
      } catch (error: any) {
        console.error("Error adding products to selection:", error);
        ToastQueue.negative(
          t("failed_to_add_products", {
            ns: "layout_components",
            defaultValue: "Failed to add products",
          }),
          { timeout: 3000 },
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      selectedProducts,
      fetchProductWithComponents,
      transformProductWithComponentsToRows,
      t,
    ],
  );

  // Handle add button click
  const handleAddSelectedProducts = useCallback(async () => {
    if (selectedProductsForAdd.size === 0) {
      ToastQueue.info(
        t("please_select_products_to_add", {
          ns: "layout_components",
          defaultValue: "Please select products to add",
        }),
        { timeout: 3000 },
      );
      return;
    }

    setIsLoading(true);
    try {
      const newSelectedRows: TreeGridRow[] = [];

      for (const productId of Array.from(selectedProductsForAdd)) {
        // Check if product is already selected
        const alreadySelected = selectedProducts.some(
          (sp) => sp.data.productId === productId || sp.id === productId,
        );

        if (alreadySelected) {
          continue;
        }

        // Fetch product with components
        const { product, components } =
          await fetchProductWithComponents(productId);

        if (product) {
          const productRows = transformProductWithComponentsToRows(
            product,
            components,
          );
          newSelectedRows.push(...productRows);
        }
      }

      if (newSelectedRows.length > 0) {
        setSelectedProducts((prev) => [...prev, ...newSelectedRows]);
        // Clear all selections from available products grid
        setSelectedProductsForAdd(new Set());
        // Force clear checkbox selections
        setSelectionClearKey((prev) => prev + 1);
        const parentCount = newSelectedRows.filter(
          (r) => r.parentId === null,
        ).length;
        ToastQueue.positive(
          t("products_added_to_selection", {
            ns: "layout_components",
            count: parentCount,
            defaultValue: "Added {{count}} product(s) to selection",
          }),
          { timeout: 3000 },
        );
      } else {
        // Clear selections even if products were already added
        setSelectedProductsForAdd(new Set());
        // Force clear checkbox selections
        setSelectionClearKey((prev) => prev + 1);
      }
    } catch (error: any) {
      console.error("Error adding products:", error);
      ToastQueue.negative(
        t("failed_to_add_products", {
          ns: "layout_components",
          defaultValue: "Failed to add products",
        }),
        { timeout: 3000 },
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedProductsForAdd,
    selectedProducts,
    fetchProductWithComponents,
    transformProductWithComponentsToRows,
    t,
  ]);

  // Handle delete from selected products
  const handleDeleteFromSelected = useCallback(
    (rowId: string, row: TreeGridRow) => {
      if (row.parentId === null) {
        setSelectedProducts((prev) =>
          prev.filter((r) => r.id !== rowId && r.parentId !== rowId),
        );
      } else {
        setSelectedProducts((prev) => {
          const newRows = prev.filter((r) => r.id !== rowId);
          return newRows.map((r) => {
            if (r.id === row.parentId) {
              return {
                ...r,
                childrenIds: r.childrenIds.filter((id: string) => id !== rowId),
              };
            }
            return r;
          });
        });
      }
      ToastQueue.info(
        _t("product_removed_from_selection", "Product removed from selection"),
        { timeout: 2000 },
      );
    },
    [],
  );

  // Handle row update (quantity change)
  const handleSelectedRowUpdate = useCallback((updatedRow: TreeGridRow) => {
    setSelectedProducts((prev) =>
      prev.map((row) => {
        if (row.id === updatedRow.id) {
          return {
            ...updatedRow,
            data: {
              ...updatedRow.data,
              quantity: parseFloat(updatedRow.data.quantity) || 1,
            },
          };
        }
        return row;
      }),
    );
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    if (selectedProducts.length === 0) {
      ToastQueue.info(
        _t("please_select_at_least_one_product", "Please select at least one product"),
        { timeout: 3000 },
      );
      return;
    }

    setIsSaving(true);
    try {
      // Get only parent rows
      const parentRows = selectedProducts.filter(
        (row) => row.parentId === null,
      );
      const productsToSave: SelectedProduct[] = parentRows.map((row) => ({
        id: row.data.productId || row.id,
        partID: row.data.productId || row.id,
        partNumber: row.data.code,
        productName: row.data.productName,
        quantity: row.data.quantity || 1,
        unitPrice: row.data.standardPrice,
        currency: row.data.currency,
        vatValue: row.data.vatPercent,
        vatName: row.data.vatName || "",
        amountWithVat: row.data.amountWithVat || 0,
        purchasePrice: row.data.purchasePrice || 0,
        productLine: row.data.productLine,
        description: row.data.description,
        components:
          row.childrenIds.length > 0
            ? selectedProducts
                .filter((r) => r.parentId === row.id)
                .map((comp) => ({
                  component: comp.data._original?.component || comp.id,
                  componentName: comp.data.productName,
                  amount: String(comp.data.standardPrice),
                  amountWithVat: String(comp.data.amountWithVat || 0),
                  purchasePrice: String(comp.data.purchasePrice || 0),
                  currencyName: comp.data.currency,
                  vatName: comp.data.vatName || "",
                  vatValue: String(comp.data.vatPercent),
                  isParent: "0",
                  countMin: String(comp.data.quantity || 1),
                  countMax: String(comp.data.quantity || 1),
                  priceInclude: "0",
                  ratio: "1",
                }))
            : undefined,
      }));

      await onSave(productsToSave);
      setSelectedProducts([]);
      setSelectedProductsForAdd(new Set());
      onClose();
    } catch (error) {
      console.error("Error saving products:", error);
    } finally {
      setIsSaving(false);
    }
  }, [selectedProducts, onSave, onClose]);

  // Handle close
  const handleClose = useCallback(() => {
    latestRequestIdRef.current += 1;
    setSelectedProducts([]);
    setSelectedProductsForAdd(new Set());
    setSearchTerm("");
    setSearchRefId("");
    setAvailableProducts([]);
    onClose();
  }, [onClose]);

  // Columns for available products grid
  const availableProductsColumns: TreeGridColumn[] = useMemo(() => {
    if (isMobileLayout) {
      // Mobile: Show only essential columns with percentage widths
      return [
        { key: "code", header: _t("code", "Code"), width: "30%", editable: false },
        {
          key: "productName",
          header: _t("product", "Product"),
          width: "70%",
          editable: false,
        },
      ];
    }
    // Desktop: Full column set
    return [
      { key: "code", header: _t("code", "Code"), width: 100, editable: false },
      {
        key: "productName",
        header: _t("product_name", "Product Name"),
        width: 250,
        editable: false,
      },
      {
        key: "standardPrice",
        header: _t("standard_price", "Standard Price"),
        width: 120,
        editable: false,
        align: "right",
        format: (value: any, row: TreeGridRow) =>
          `${parseFloat(value || 0).toFixed(2)} ${row.data.currency}`,
      },
      {
        key: "vatPercent",
        header: _t("vat_percent", "VAT %"),
        width: 80,
        editable: false,
        align: "right",
      },
      {
        key: "productLine",
        header: _t("product_line", "Product Line"),
        width: 150,
        editable: false,
      },
      { key: "description", header: _t("description", "Description"), editable: false },
    ];
  }, [isMobileLayout, _t]);

  // Columns for selected products grid
  const selectedProductsColumns: TreeGridColumn[] = useMemo(() => {
    if (isMobileLayout) {
      // Mobile: Show essential columns with percentage widths
      return [
        {
          key: "code",
          header: _t("code", "Code"),
          width: "25%",
          editable: false,
          isTreeColumn: true,
          showFolderIcon: true,
        },
        {
          key: "productName",
          header: _t("product", "Product"),
          width: "45%",
          editable: false,
        },
        {
          key: "quantity",
          header: _t("qty", "Qty"),
          width: "15%",
          editable: false,
          align: "right",
          numberFormat: { decimals: 2 },
        },
        {
          key: "standardPrice",
          header: _t("price", "Price"),
          width: "15%",
          editable: false,
          align: "right",
          format: (value: any, row: TreeGridRow) =>
            `${parseFloat(value || 0).toFixed(2)} ${row.data.currency}`,
        },
      ];
    }
    // Desktop: Full column set
    return [
      {
        key: "code",
        header: _t("code", "Code"),
        width: 100,
        editable: false,
        isTreeColumn: true,
        showFolderIcon: true,
      },
      {
        key: "productName",
        header: _t("product_name", "Product Name"),
        width: 250,
        editable: false,
      },
      {
        key: "quantity",
        header: _t("quantity", "Quantity"),
        width: 80,
        editable: false,
        align: "right",
        numberFormat: { decimals: 2 },
      },
      {
        key: "standardPrice",
        header: _t("unit_price", "Unit Price"),
        width: 120,
        editable: false,
        align: "right",
        format: (value: any, row: TreeGridRow) =>
          `${parseFloat(value || 0).toFixed(2)} ${row.data.currency}`,
      },
      {
        key: "vatPercent",
        header: _t("vat_percent", "VAT %"),
        width: 80,
        editable: false,
        align: "right",
      },
    ];
  }, [isMobileLayout, _t]);

  return (
    <DialogContainer onDismiss={handleClose}>
      {isOpen && (
        <Dialog
          size={isMobileLayout ? "M" : "L"}
          width={isMobileLayout ? "100vw" : "95vw"}
          height={isMobileLayout ? "100vh" : desktopDialogHeight}
        >
          <Heading>{_t("select_products", "Select Products")}</Heading>
          <Content UNSAFE_style={{ overflow: "hidden" }}>
            <Flex
              direction="column"
              height="100%"
              UNSAFE_style={{ padding: isMobileLayout ? "8px" : "12px" }}
            >
              {/* Scrollable content area */}
              <View flex={1} UNSAFE_style={{ overflowY: "auto", minHeight: 0 }}>
                <Flex direction="column" gap={isMobileLayout ? "size-150" : "size-100"}>
                  {/* Search and Filter */}
                  <Flex
                    direction={isMobileLayout ? "column" : "row"}
                    gap={isMobileLayout ? "size-150" : "size-100"}
                    alignItems={isMobileLayout ? "stretch" : "end"}
                  >
                    <Picker
                      label={_t("price_list", "Price List")}
                      selectedKey={priceListFilter}
                      onSelectionChange={(key) =>
                        setSelectedPriceList(key as string)
                      }
                      width={isMobileLayout ? "100%" : "size-2400"}
                    >
                      <Item key="all">{_t("all_price_lists", "All Price Lists")}</Item>
                    </Picker>
                    <TextField
                      label={_t("search_by_name", "Search by Name")}
                      placeholder={_t(
                        "search_by_product_name_placeholder",
                        "Search by product name...",
                      )}
                      value={searchTerm}
                      onChange={setSearchTerm}
                      flex={isMobileLayout ? undefined : 1}
                      width={isMobileLayout ? "100%" : undefined}
                    />
                    <TextField
                      label={_t("search_by_ref_id", "Search by Ref ID")}
                      placeholder={_t(
                        "search_by_ref_id_placeholder",
                        "Search by ref ID...",
                      )}
                      value={searchRefId}
                      onChange={setSearchRefId}
                      flex={isMobileLayout ? undefined : 1}
                      width={isMobileLayout ? "100%" : undefined}
                    />
                  </Flex>

                  {/* Available Products Grid */}
                  <View
                    UNSAFE_style={{
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      overflow: "hidden",
                      minHeight: isMobileLayout ? "260px" : `${desktopGridHeight}px`,
                    }}
                  >
                    <TreeGridComponent
                      key={`available-products-${selectionClearKey}`}
                      title={_t(
                        "available_products_catalog",
                        "Available Products (Catalog)",
                      )}
                      gridId="available-products"
                      columns={availableProductsColumns}
                      data={availableProducts}
                      showSelection
                      showSelectAll
                      enableDragDrop={false}
                      disableInternalDrop
                      enableKeyboardNav
                      pageSize={PAGE_SIZE}
                      indentPerLevel={0}
                      maxDepth={1}
                      showGridLines
                      showHover
                      showToolbar={false}
                      showInfoIcon={false}
                      showDeleteIcon={false}
                      totalRows={totalCount}
                      hasMore={hasMore}
                      isLoadingMore={isLoadingMore}
                      onLoadMore={loadMoreProducts}
                      maxHeight={isMobileLayout ? 400 : desktopGridHeight}
                      emptyMessage={
                        isLoading
                          ? _t("loading_products", "Loading products...")
                          : _t("no_products_available", "No products available")
                      }
                      onSelectionChange={(selectedKeys: string[]) =>
                        setSelectedProductsForAdd(new Set(selectedKeys))
                      }
                    />
                  </View>

                  {/* Add Button */}
                  <Flex justifyContent="center" marginTop={isMobileLayout ? "size-100" : "size-50"}>
                    <Button
                      variant="accent"
                      onPress={handleAddSelectedProducts}
                      isDisabled={
                        selectedProductsForAdd.size === 0 || isLoading
                      }
                      width={isMobileLayout ? "100%" : undefined}
                    >                      {_t(
                        "add_selected_products_count",
                        "Add Selected Products ({{count}})",
                        { count: selectedProductsForAdd.size },
                      )}
                    </Button>
                  </Flex>

                  {/* Selected Products Grid */}
                  <View
                    UNSAFE_style={{
                      border: "2px solid #4caf50",
                      borderRadius: "8px",
                      overflow: "hidden",
                      boxShadow: "0 0 0 2px rgba(76, 175, 80, 0.2)",
                      minHeight: selectedGridMinHeight,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <View flex={selectedProductsParentCount === 0 ? undefined : 1}>
                      <TreeGridComponent
                        title={_t(
                          "selected_products_count",
                          "Selected Products ({{count}} items)",
                          { count: selectedProductsParentCount },
                        )}
                        gridId="selected-products"
                        columns={selectedProductsColumns}
                        data={selectedProducts}
                        showSelection={false}
                        showSelectAll={false}
                        enableDragDrop={false}
                        enableKeyboardNav
                        enableInlineEdit
                        pageSize={20}
                        indentPerLevel={32}
                        maxDepth={2}
                        showGridLines
                        showHover
                        showToolbar={false}
                        showInfoIcon={false}
                        showDeleteIcon
                        acceptExternalDrop
                        acceptDropFromGridIds={["available-products"]}
                        onExternalDrop={handleExternalDropToSelected}
                        onDeleteClick={handleDeleteFromSelected}
                        onRowUpdate={handleSelectedRowUpdate}
                        maxHeight={
                          isMobileLayout
                            ? 400
                            : selectedProductsParentCount === 0
                              ? emptySelectedGridHeight
                              : desktopGridHeight
                        }
                        emptyMessage={_t(
                          "drag_products_here_or_use_add_button",
                          "Drag products here or use the Add button to select them for the project",
                        )}
                        deleteConfirmationMessage={_t(
                          "remove_product_confirmation",
                          "Are you sure you want to remove this product from selection?",
                        )}
                        deleteConfirmationTitle={_t(
                          "remove_product",
                          "Remove product",
                        )}
                      />
                    </View>
                  </View>
                </Flex>
              </View>

              {/* Footer with counts */}
              <Flex
                direction={isMobileLayout ? "column" : "row"}
                justifyContent={isMobileLayout ? "stretch" : "space-between"}
                alignItems={isMobileLayout ? "stretch" : "center"}
                gap="size-150"
                UNSAFE_style={{
                  flexShrink: 0,
                  borderTop: "1px solid #e0e0e0",
                  paddingTop: isMobileLayout ? "12px" : "8px",
                }}
              >
                <View>
                  {t("available_x_of_y_selected_z_products", {
                    ns: "layout_components",
                    defaultValue:
                      "Available: {{available}} of {{total}} | Selected: {{selected}} product(s)",
                    available: availableProducts.length,
                    total: totalCount,
                    selected: selectedProductsParentCount,
                  })}
                </View>
                <Flex
                  direction={isMobileLayout ? "column" : "row"}
                  gap="size-150"
                  width={isMobileLayout ? "100%" : undefined}
                >
                  <Button
                    variant="secondary"
                    onPress={handleClose}
                    isDisabled={isSaving}
                    width={isMobileLayout ? "100%" : undefined}
                    UNSAFE_style={{
                      backgroundColor: "#ffebee",
                      color: "#d32f2f",
                      border: "none",
                      borderRadius: "8px",
                      padding: isMobileLayout ? "10px 20px" : "12px 24px",
                      fontSize: isMobileLayout ? "14px" : "16px",
                      fontWeight: 600,
                    }}
                  >
                    {t("cancel", {
                      ns: "layout_components",
                      defaultValue: "Cancel",
                    })}
                  </Button>
                  <Button
                    variant="cta"
                    onPress={handleSave}
                    isDisabled={selectedProducts.length === 0 || isSaving}
                    width={isMobileLayout ? "100%" : undefined}
                    UNSAFE_style={{
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      border: "none",
                      borderRadius: "8px",
                      padding: isMobileLayout ? "10px 20px" : "12px 24px",
                      fontSize: isMobileLayout ? "14px" : "16px",
                      fontWeight: 600,
                    }}
                  >
                    {isSaving
                      ? t("saving", {
                          ns: "layout_components",
                          defaultValue: "Saving...",
                        })
                      : t("submit_and_close_count", {
                          ns: "layout_components",
                          defaultValue: "Submit & Close ({{count}})",
                          count: selectedProductsParentCount,
                        })}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </Content>
        </Dialog>
      )}
    </DialogContainer>
  );
}
